<template>
    <page-layout>
        <template #header>
            <page-title :title="pageTitle" @on-close="handleBack" />
        </template>
        <g-loader v-if="isLoading" type="fixed" />
        <template v-else>
            <g-button class="margin-bottom-large" icon="angle-left" theme="live" size="small" @click="handleBack">
                Back
            </g-button>

            <g-typography tag="h4" font-weight="semibold" class="margin-bottom-small" value="Provide Metrics" />

            <g-typography tag="p" class="margin-bottom-large">
                <template v-if="task">
                    Provide the metrics for your {{ NETWORK_LABEL_MAP[task.type] }} by entering them manually or uploading a
                    screenshot of the metrics from {{ NETWORK_INFO_BY_ID[task.network_id].name }}.
                </template>
                <template v-else>
                    Provide the metrics for your content by entering them manually or uploading a
                    screenshot of the metrics from {{ getNetworkName() }}.
                </template>
            </g-typography>

            <g-form-item label="Choose a metrics input method" class="margin-bottom-normal">
                <g-radio-group v-model="inputMethod">
                    <g-radio :value="CONTENT_METRIC_INPUT_SCREENSHOT" size="large">
                        <g-typography>Screenshots</g-typography>
                    </g-radio>

                    <g-radio :value="CONTENT_METRIC_INPUT_MANUAL" size="large">
                        <g-typography>Manual Entry</g-typography>
                    </g-radio>
                </g-radio-group>
            </g-form-item>

            <div v-if="inputMethod === CONTENT_METRIC_INPUT_SCREENSHOT">
                <activation-content-metrics-screenshot
                    :content="content"
                    :task="task"
                    :is-loading="isLoading"
                    :is-file-uploading="isFileUploading"
                    :is-file-deleting="isFileDeleting"
                    :is-submitting="isSubmitting"
                    :upload-screenshot-url="uploadScreenshotUrl"
                    :upload-headers="uploadHeaders"
                    :uploaded-files="uploadedFiles"
                    :handle-before-upload-validation="handleBeforeUploadValidation"
                    @file-change="handleFileChange"
                    @reset-uploads="handleResetUploads"
                    @screenshots-submitted="handleScreenshotsSubmitted"
                    @file-deleted="handleFileDeleted"
                />
            </div>
            <div v-else-if="inputMethod === CONTENT_METRIC_INPUT_MANUAL">
                <activation-content-metrics-manual
                    :content="content"
                    :is-submitting="isSubmitting"
                    :is-loading="isLoading"
                    @update-content-metrics="handleUpdateContentMetrics"
                />
            </div>
        </template>
    </page-layout>
</template>

<script>
    import { ref } from 'vue-demi';
    import { NETWORK_INFO_BY_ID, NETWORK_LABEL_MAP } from '../../../../../constants/networks';
    import {
        CONTENT_METRIC_INPUT_SCREENSHOT,
        CONTENT_METRIC_INPUT_MANUAL
    } from '../../../../../constants/activation-content-metrics';
    import PageLayout from '../../../../layouts/PageLayout.vue';
    import PageTitle from '../../../../library/PageTitle.vue';
    import ActivationContentMetricsManual from './ActivationContentMetricsManual.vue';
    import ActivationContentMetricsScreenshot from './ActivationContentMetricsScreenshot.vue';

    export default {
        name: 'ActivationContentMetricsView',

        components: {
            PageLayout,
            PageTitle,
            ActivationContentMetricsManual,
            ActivationContentMetricsScreenshot
        },

        props: {
            brief: {
                type: Object,
                default: null
            },
            task: {
                type: Object,
                default: null
            },
            content: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isSubmitting: {
                type: Boolean,
                default: false
            },
            uploadScreenshotUrl: {
                type: String,
                default: ''
            },
            uploadHeaders: {
                type: Object,
                default: null
            },
            uploadedFiles: {
                type: Array,
                default: null
            },
            isFileUploading: {
                type: Boolean,
                default: false
            },
            isFileDeleting: {
                type: Boolean,
                default: false
            },
            handleBeforeUploadValidation: {
                type: Function,
                default: () => {}
            }
        },

        setup(props, { emit }) {
            const inputMethod = ref(CONTENT_METRIC_INPUT_SCREENSHOT);

            const handleBack = () => {
                emit('back');
            };

            const handleUpdateContentMetrics = (id, input) => {
                emit('update-content-metrics', id, input);
            };

            const handleFileChange = (info) => {
                emit('file-change', info);
            };

            const handleResetUploads = () => {
                emit('reset-uploads');
            };

            const handleScreenshotsSubmitted = (payload) => {
                emit('screenshots-submitted', payload);
            };

            const handleFileDeleted = (fileName) => {
                emit('file-deleted', fileName);
            };

            const getNetworkName = () => {
                // For taskless content, get network name from content object
                if (props.content && props.content.network_id) {
                    return NETWORK_INFO_BY_ID[props.content.network_id]?.name || 'Instagram';
                }
                return 'Instagram'; // Default fallback
            };

            const pageTitle = props.task ? 'Deliver Task' : 'Deliver Metrics';

            return {
                NETWORK_INFO_BY_ID,
                NETWORK_LABEL_MAP,
                CONTENT_METRIC_INPUT_SCREENSHOT,
                CONTENT_METRIC_INPUT_MANUAL,
                inputMethod,
                handleBack,
                handleUpdateContentMetrics,
                handleFileChange,
                handleResetUploads,
                handleScreenshotsSubmitted,
                handleFileDeleted,
                getNetworkName,
                pageTitle
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }
</style>
